/**
 * Owner command-door bindings — the pure seam behind the pairing/revoke UI.
 *
 * The command door for a channel is a VERIFIED peer binding on the owner record
 * (`contact-owner.channels[]`). The runtime's `ownerPeers()` reads exactly the
 * verified rows for a channel, and `channelComplete[ch]` requires one to exist —
 * so "does a command door exist for this channel" is precisely "is there a
 * verified binding for it".
 *
 * ## REPLACE mode (runtime, 2026-07-24)
 *
 * A user has exactly ONE command-door account per channel — pairing a new
 * account REPLACES the incumbent atomically. So a channel has AT MOST one
 * verified binding, and a second pairing REVOKES the first rather than adding
 * alongside it. The UI must say "replace", not "add", once a door exists.
 *
 * ## The silent-no-op trap
 *
 * `contacts.unbind_peer` filters the owner's channel array by
 * `(channel, peerId)` and returns success even when it matched nothing — so a
 * `peerId` that does not match byte-for-byte leaves the owner bound while the
 * call reports 200. `peerId` format differs per channel (E.164-with-plus for
 * WhatsApp, a bare chat id for Telegram) and must never be normalised. The
 * caller therefore passes the value straight from `owner.channels[]` and
 * ASSERTS the postcondition on the returned contact — `bindingCleared` below is
 * that assertion.
 */

import type { Contact, ContactChannelIdentity } from "@/types/contacts";

/**
 * The verified command-door binding for a channel, or `null` if the door is
 * open (unpaired). Under REPLACE mode there is at most one, so `find` is exact
 * rather than lossy.
 */
export function verifiedBindingFor(
  // Typed to the ONE field this reads, not to `Contact`, so it also accepts an
  // owner draft that has no server-assigned provenance yet (first-run, before
  // any `identity.set`). A binding question is answerable from `channels`
  // alone; demanding a full Contact would only force a caller to fabricate
  // createdAt/updatedAt to ask it.
  owner: Pick<Contact, "channels"> | null,
  channel: string,
): ContactChannelIdentity | null {
  if (!owner) return null;
  return (
    owner.channels.find((ch) => ch.channel === channel && ch.verified) ?? null
  );
}

/** Does a channel currently have a command door (a verified binding)? */
export function hasCommandDoor(
  owner: Pick<Contact, "channels"> | null,
  channel: string,
): boolean {
  return verifiedBindingFor(owner, channel) != null;
}

/**
 * Postcondition check for a revoke: is the exact `(channel, peerId)` binding
 * GONE from the contact the unbind call returned? A `false` here is the silent
 * no-op — the call succeeded but matched nothing, so the owner is still bound
 * and the caller must surface a failure rather than report "disconnected".
 *
 * Compares `peerId` verbatim (no trim/normalise) on purpose: the runtime stored
 * it verbatim, and a "helpful" reformat here would mask the very mismatch this
 * guard exists to catch.
 */
export function bindingCleared(
  returnedContact: Contact,
  channel: string,
  peerId: string,
): boolean {
  return !returnedContact.channels.some(
    (ch) => ch.channel === channel && ch.peerId === peerId,
  );
}
